import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

import {
	getTotalInsuredAmount,
	isEndorsement,
	getBinderNumber,
	getPolicyFact,
	getAssured,
	getCountry,
	getInsuredAmountCurrency,
	getPolicyFromTransactionObj,
	formatDate,
	getTransactionTypeHKReport,
	getCommissionRatio,
	getCommissionAmount
} from "./helpers"

const header = [
	[
		"Unique Market Reference",
		"Agreement No.",
		"Year of Account",
		"Accounting Class",
		"Accounting Sub-Class",
		"Direct or Type of Re-insurance",
		"Policy or Group Reference",
		"Certificate Reference",
		"Name or Company Name",
		"Country",
		"Trade Occupation",
		"Type of Vehicle",
		"No. of Vessels",
		"Risk Inception Date",
		"Risk Expiry Date",
		"Location of Risk Country",
		"Currency",
		"Amount",
		"Transaction Type",
		"Endorsement Effective Date",
		"Endorsement Expiry Date",
		"Original Currency",
		"Gross Written Premium",
		"Estimated Premium Income",
		"Coverholder Commission Amount",
		"Coverholder Commission %",
		"Local Sub-producer's Commission Amount",
		"Local sub-producer's Commission %",
		"Lloyd's Brokerage Amount",
		"Lloyd's Brokerage Commission %",
		"Net Premium to Underwriters",
		"Management Expenses",
		"Co-insurance",
		"Total Gross Written Premium Amount",
		"Levy Amount"
	]
]

/****************** Helper Functions ******************/
const getTotalLevy = policy => {
	return policy?.premium?.appliedTaxes
		?.filter(
			item =>
				item.name === "hkIALevy" ||
				item.name === "IA Levy" ||
				item.name === "ecLevy" ||
				item.name === "eciibContribution" ||
				item.name === "ecGovTerrorismFacilityCharge"
		)
		.reduce((sum, item) => {
			return (sum += item.flat)
		}, 0)
}

const getRiskCountries = policy => {
	const type = policy?.product?.productId?.type
	const assured = getAssured(policy)

	// EX - fact type is location on insured
	if (type === "jewellers_exhibition") {
		return policy?.contractInsured
			?.map(
				item =>
					item.facts?.find(fact => fact.type === "location")?.value?.stringValue
			)
			?.join(",")
	}
	// TO - fact type tourCountry on insured
	if (type === "jewellers_tour") {
		return policy?.contractInsured
			?.map(
				item =>
					item.facts?.find(fact => fact.type === "tourCountry")?.value
						?.stringValue
			)
			?.join(",")
	}
	// SI - fact types are voyageOrigin, voyageVia, voyageDestination on insured
	if (type === "sending") {
		return [
			...policy?.contractInsured?.map(
				item =>
					item.facts?.find(fact => fact.type === "voyageOrigin")?.value
						?.stringValue
			),
			...policy?.contractInsured?.map(
				item =>
					item.facts?.find(fact => fact.type === "voyageVia")?.value
						?.stringValue
			),
			...policy?.contractInsured?.map(
				item =>
					item.facts?.find(fact => fact.type === "voyageDestination")?.value
						?.stringValue
			)
		]?.join(",")
	} else return getCountry(assured)
}

const getBinderYearOfAccount = policy => {
	const startDate = policy?.associatedContracts?.[0]?.contract?.startDate
	return dayjs(startDate).format("YYYY")
}

/****************** Define Rows ******************/
const rows = fetchedData => {
	let output = []
	fetchedData?.reverse()?.map(transaction => {
		const policy = getPolicyFromTransactionObj(transaction)
		const netPremium =
			transaction?.amount -
			getCommissionAmount("coverholder", transaction?.amount, policy) -
			getCommissionAmount("broker", transaction?.amount, policy)

		const assured = getAssured(policy)

		output.push([
			getBinderNumber(policy),
			"",
			getBinderYearOfAccount(policy),
			getPolicyFact("accountingClass", policy),
			getPolicyFact("accountingSubClass", policy),
			"Direct",
			"",
			policy?.issuerNumber,
			assured?.name,
			getCountry(assured),
			"",
			"",
			"",
			policy?.startDate ? formatDate(policy?.startDate) : "",
			policy?.startDate ? formatDate(policy?.endDate) : "",
			getRiskCountries(policy),
			getInsuredAmountCurrency(policy),
			getTotalInsuredAmount(policy),
			getTransactionTypeHKReport(transaction),
			isEndorsement(transaction) && policy?.startDate
				? formatDate(policy.startDate)
				: "",
			isEndorsement(transaction) && policy?.endDate
				? formatDate(policy.endDate)
				: "",
			transaction?.currencyCode,
			transaction?.amount,
			"",
			getCommissionAmount("coverholder", transaction?.amount, policy),
			getCommissionRatio("coverholder", policy) * 100,
			"",
			"",
			getCommissionAmount("broker", transaction?.amount, policy),
			getCommissionRatio("broker", policy) * 100,
			netPremium,
			"",
			getPolicyFact("coInsurance", policy),
			"",
			getTotalLevy(policy)
		])
	})

	return output
}

/****************** Function for fetching ******************/
const fetchTransactions = async filterPayload => {
	const dateFrom = filterPayload?.issueDate?.after
		? dayjs(filterPayload?.issueDate?.after).format()
		: null
	const dateTo = filterPayload?.issueDate?.before
		? dayjs(filterPayload?.issueDate?.before)
				.add(1, "day")
				.format()
		: null

	const query = gql`
		fragment obj on policy {
			associatedContracts {
				contract {
					startDate
					endDate
					facts {
						id
						type
						value {
							stringValue
						}
					}
				}
			}
			stakeholders {
				entity {
					internalCode
				}
				type
			}
			proposal {
				renewalHistory {
					renewedFrom {
						id
					}
				}
			}
			facts {
				id
				type
				value {
					stringValue
					numberValue
					dateValue
				}
			}
			contractInsured {
				__typename
				name
				addresses {
					type
					fields2 {
						type
						value
					}
				}
				facts {
					type
					value {
						stringValue
					}
				}
			}
			contractHolder {
				__typename
				name
				addresses {
					type
					fields2 {
						type
						value
					}
				}
			}
			startDate
			endDate
			transactions {
				facts {
					type
					value {
						stringValue
					}
				}
			}
			commissionSplitRules {
				type
				currencyCode
				amount
				remark
				ratio
			}
			premium {
				currencyCode
				amount
				originalPrice
				appliedTaxes {
					name
					flat
				}
			}
			valuesJsonString
			issueDate
			product {
				productId {
					type
				}
				pricing {
					currencyCode
				}
			}
			issuerNumber
		}
		query fetchTransactions($transactionWhere: transactionWhereInput) {
			transactions(where: $transactionWhere) {
				list {
					amount
					currencyCode
					dateTime
					status
					object {
						__typename
						... on policy {
							...obj
							endorsements {
								beforeEndorsement {
									...obj
								}
							}
						}
						... on endorsement {
							afterEndorsement {
								...obj
							}
						}
					}
				}
			}
		}
	`
	const variables = {
		transactionWhere: {
			and: [
				{ dateTime_gt: dateFrom },
				{ dateTime_lt: dateTo },
				{
					or: [
						{ status: "APPROVED" },
						{ status: "REFUNDED" },
						{ status: "CANCELLED_BY_USER" }
					]
				}
			]
		}
	}

	const res = await api({ query, variables })
	//TODO: error handling
	return res?.data?.transactions?.list
}

/****************** Export ******************/
export default async filterPayload => {
	const fetchedData = await fetchTransactions(filterPayload)
	const workSheetData = [...header, ...rows(fetchedData)]
	const workSheetColumns = header?.[0]?.map(item => ({ wpx: 200 }))
	return { workSheetData, workSheetColumns }
}
